/** User-facing LiveKit join / connect errors. */

/** Backend join guard copy (livekit_join_guard.py). */
const JOIN_REJECTION_ENDED =
  "this session has ended and can no longer be joined";
const JOIN_REJECTION_CANCELLED =
  "this session was cancelled and can no longer be joined";

export const SESSION_ENDED_USER_MESSAGE = "Session has been ended";
export const SESSION_CANCELLED_USER_MESSAGE =
  "This session was cancelled and can no longer be joined.";

export function extractLiveKitApiErrorMessage(err: unknown): string | null {
  const ax = err as {
    response?: { data?: { detail?: unknown; message?: unknown } };
  };
  if (!ax.response) {
    return null;
  }
  const detail = ax.response.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail.trim();
  }
  if (detail && typeof detail === "object") {
    const nested = (detail as { message?: unknown }).message;
    if (typeof nested === "string" && nested.trim()) {
      return nested.trim();
    }
  }
  const top = ax.response.data?.message;
  if (typeof top === "string" && top.trim()) {
    return top.trim();
  }
  return null;
}

function unwrapValidationDetail(text: string): string {
  const match = text.match(/Validation error for field '[^']+': (.+)/i);
  return match?.[1]?.trim() || text;
}

function normalizeJoinRejection(text: string): string {
  return unwrapValidationDetail(text).toLowerCase().trim();
}

export function isSessionCancelledJoinRejectionMessage(
  message: string,
): boolean {
  const normalized = normalizeJoinRejection(message);
  return (
    normalized === SESSION_CANCELLED_USER_MESSAGE.toLowerCase() ||
    normalized.includes(JOIN_REJECTION_CANCELLED)
  );
}

/** True only when the LMS rejected join because the session is finished. */
export function isSessionEndedJoinRejectionMessage(message: string): boolean {
  const normalized = normalizeJoinRejection(message);
  return (
    normalized === SESSION_ENDED_USER_MESSAGE.toLowerCase() ||
    normalized.includes(JOIN_REJECTION_ENDED)
  );
}

/** Soft "room still preparing" — keep polling; not a hard failure. */
export function isLiveKitRoomStartingMessage(message: string): boolean {
  const normalized = normalizeJoinRejection(message);
  return (
    normalized.includes("not ready") ||
    normalized.includes("still starting") ||
    normalized.includes("is preparing") ||
    normalized.includes("not provisioned") ||
    normalized.includes("trainer must start") ||
    normalized.includes("wait for the trainer") ||
    normalized.includes("wait for the green pod") ||
    normalized.includes("media pod is not ready") ||
    normalized.includes("try again shortly") ||
    normalized.includes("try again in a moment") ||
    normalized.includes("re-start the session so the media pod") ||
    // Student paused copy must never block trainer resume — treat as preparing.
    normalized.includes("join opens after the trainer resumes") ||
    (normalized.includes("session is paused") &&
      normalized.includes("expected resume"))
  );
}

export function friendlyLiveKitConnectError(err: unknown): string {
  const ax = err as {
    message?: string;
    response?: unknown;
  };

  if (ax.response) {
    const apiMsg = extractLiveKitApiErrorMessage(err);
    if (apiMsg) {
      const plain = unwrapValidationDetail(apiMsg);
      if (isSessionCancelledJoinRejectionMessage(plain)) {
        return SESSION_CANCELLED_USER_MESSAGE;
      }
      if (isSessionEndedJoinRejectionMessage(plain)) {
        return SESSION_ENDED_USER_MESSAGE;
      }
      return plain;
    }
  }

  const raw =
    err instanceof Error ? err.message : "Unable to connect to LiveKit";
  const lower = raw.toLowerCase();
  if (!ax.response) {
    const offline =
      (typeof navigator !== "undefined" && navigator.onLine === false) ||
      lower.includes("network error") ||
      lower.includes("failed to fetch") ||
      lower.includes("networkerror") ||
      lower.includes("err_internet_disconnected") ||
      lower.includes("err_network");
    if (offline) return "check your internet";
    if (
      lower.includes("404") ||
      lower.includes("not found") ||
      lower.includes("could not establish") ||
      lower.includes("connectionerror") ||
      lower.includes("failed to connect") ||
      lower.includes("websocket")
    ) {
      return "Live room is not available yet. Wait for the media server, then try again.";
    }
  }

  return raw;
}

export function isSessionEndedLiveKitError(
  message: string | null | undefined,
): boolean {
  if (!message) return false;
  return isSessionEndedJoinRejectionMessage(message);
}
